/**
 * Shared Access + Add-ons present/absent checklist (ALI + Lead Details).
 * Simple solid ops UI: teal accent, Present | Absent | Unknown segments.
 */

import React, { useMemo } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { buildElementChecklistRows } from "./elementChecklistHelpers.js";

const TEAL = "#0F766E";

const statusChipColor = (status) => {
  if (status === "present") return "success";
  if (status === "absent") return "default";
  return "warning";
};

const StatusSegment = ({ id, status, disabled, onChange }) => (
  <ToggleButtonGroup
    exclusive
    fullWidth
    size="small"
    value={status}
    disabled={disabled}
    onChange={(_e, next) => {
      if (next) onChange(next);
    }}
    data-testid={`element-status-${id}`}
    sx={{
      width: "100%",
      "& .MuiToggleButton-root": {
        flex: 1,
        minHeight: 40,
        textTransform: "none",
        borderColor: "divider",
        px: 1,
        fontSize: "0.8rem",
      },
      "& .MuiToggleButton-root.Mui-selected": {
        bgcolor:
          status === "present"
            ? TEAL
            : status === "unknown"
              ? "primary.main"
              : undefined,
        color:
          status === "present" || status === "unknown" ? "#fff" : undefined,
        "&:hover": {
          bgcolor:
            status === "present"
              ? "#0d6b63"
              : status === "unknown"
                ? "primary.dark"
                : undefined,
        },
      },
    }}
  >
    <ToggleButton value="present">Present</ToggleButton>
    <ToggleButton value="absent">Absent</ToggleButton>
    <ToggleButton value="unknown">Unknown</ToggleButton>
  </ToggleButtonGroup>
);

const ValueField = ({ row, disabled, onChange }) => {
  if (row.status !== "present") return null;
  if (row.control === "quantity" || row.control === "number") {
    return (
      <TextField
        type="number"
        size="small"
        label={row.id.startsWith("access:stairs") ? "Floors" : "Quantity"}
        value={row.value ?? ""}
        disabled={disabled}
        onChange={(e) => {
          const n = e.target.value === "" ? "" : Number(e.target.value);
          onChange(n);
        }}
        inputProps={{
          min: row.min ?? 0,
          max: row.max,
          step: row.step ?? 1,
        }}
        sx={{ mt: 1, maxWidth: 160 }}
      />
    );
  }
  if (row.control === "select" && Array.isArray(row.options) && row.options.length) {
    return (
      <TextField
        select
        size="small"
        label="Option"
        value={row.value ?? ""}
        disabled={disabled}
        onChange={(e) => onChange(e.target.value)}
        SelectProps={{ native: true }}
        sx={{ mt: 1, maxWidth: 220 }}
      >
        <option value="" />
        {row.options.map((opt) => {
          const val = typeof opt === "object" ? opt.value ?? opt.id : opt;
          const label = typeof opt === "object" ? opt.label ?? val : opt;
          return (
            <option key={String(val)} value={String(val)}>
              {label}
            </option>
          );
        })}
      </TextField>
    );
  }
  return null;
};

const ChecklistRow = ({ row, mode, onStatusChange, onValueChange }) => {
  const theme = useTheme();
  const isNarrow = useMediaQuery(theme.breakpoints.down("sm"));
  const editable = mode === "edit";

  if (!editable) {
    return (
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        spacing={1}
        data-testid={`element-row-${row.id}`}
        sx={{ py: 1 }}
      >
        <Box>
          <Typography variant="body2">{row.name}</Typography>
          {row.status === "present" &&
          row.value !== undefined &&
          row.value !== null &&
          row.value !== true ? (
            <Typography variant="caption" color="text.secondary">
              {row.id.startsWith("access:stairs")
                ? `Floor ${row.value}`
                : `qty ${row.value}`}
            </Typography>
          ) : null}
        </Box>
        <Chip
          size="small"
          label={
            row.status === "present"
              ? "Present"
              : row.status === "absent"
                ? "Absent"
                : "Unknown"
          }
          color={statusChipColor(row.status)}
          variant={row.status === "absent" ? "outlined" : "filled"}
          data-testid={`element-status-${row.id}`}
        />
      </Stack>
    );
  }

  return (
    <Box data-testid={`element-row-${row.id}`} sx={{ py: 1.25 }}>
      <Stack
        direction={isNarrow ? "column" : "row"}
        spacing={1}
        alignItems={isNarrow ? "stretch" : "center"}
        justifyContent="space-between"
      >
        <Typography variant="body2" sx={{ minWidth: isNarrow ? 0 : 160 }}>
          {row.name}
        </Typography>
        <Box sx={{ flex: 1, maxWidth: isNarrow ? "100%" : 360 }}>
          <StatusSegment
            id={row.id}
            status={row.status}
            onChange={(next) => onStatusChange(row.id, next)}
          />
        </Box>
      </Stack>
      <ValueField
        row={row}
        onChange={(next) => onValueChange(row.id, next)}
      />
    </Box>
  );
};

/**
 * @param {{
 *   mode?: "edit"|"readonly",
 *   elements?: object[],
 *   confirmations?: unknown,
 *   pricingSelections?: unknown,
 *   card?: object|null,
 *   loading?: boolean,
 *   error?: string|null,
 *   onChange?: (confirmations: object[]) => void,
 *   showAcceptGate?: boolean,
 * }} props
 */
const LeadElementChecklist = ({
  mode = "readonly",
  elements = [],
  confirmations,
  pricingSelections,
  card = null,
  loading = false,
  error = null,
  onChange,
  showAcceptGate = false,
}) => {
  const theme = useTheme();
  const isMdUp = useMediaQuery(theme.breakpoints.up("md"));

  const { accessRows, addonRows, incompleteAccess, unknownAddons } = useMemo(
    () =>
      buildElementChecklistRows({
        elements,
        confirmations,
        pricingSelections,
      }),
    [elements, confirmations, pricingSelections],
  );

  const emitChange = (nextRows) => {
    if (!onChange) return;
    onChange(
      nextRows.map((row) => {
        const entry = { id: row.id, status: row.status };
        if (row.status === "present" && row.value !== undefined) {
          entry.value = row.value;
        }
        return entry;
      }),
    );
  };

  const allRows = [...accessRows, ...addonRows];

  const handleStatusChange = (id, status) => {
    const next = allRows.map((row) =>
      row.id === id
        ? {
            ...row,
            status,
            value: status === "present" ? row.value : undefined,
          }
        : row,
    );
    emitChange(next);
  };

  const handleValueChange = (id, value) => {
    const next = allRows.map((row) =>
      row.id === id ? { ...row, value, status: "present" } : row,
    );
    emitChange(next);
  };

  const handleMarkAddonsAbsent = () => {
    const next = allRows.map((row) =>
      row.isAccess ? row : { ...row, status: "absent", value: undefined },
    );
    emitChange(next);
  };

  if (loading) {
    return (
      <Box
        data-testid="lead-element-checklist"
        sx={{ display: "flex", justifyContent: "center", py: 2 }}
      >
        <CircularProgress size={22} />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" data-testid="lead-element-checklist">
        {error}
      </Alert>
    );
  }

  if (accessRows.length === 0 && addonRows.length === 0) {
    return (
      <Typography
        variant="body2"
        color="text.secondary"
        data-testid="lead-element-checklist"
      >
        No live-card access or add-on elements available.
      </Typography>
    );
  }

  const section = (title, rows, toolbar = null) => (
    <Box sx={{ mb: 2 }}>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{ mb: 0.5 }}
      >
        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
          {title}
        </Typography>
        {toolbar}
      </Stack>
      <Divider sx={{ mb: 0.5 }} />
      {rows.map((row) => (
        <ChecklistRow
          key={row.id}
          row={row}
          mode={mode}
          onStatusChange={handleStatusChange}
          onValueChange={handleValueChange}
        />
      ))}
    </Box>
  );

  return (
    <Box data-testid="lead-element-checklist">
      <Stack
        direction="row"
        spacing={1}
        alignItems="center"
        sx={{ mb: 1.5 }}
        flexWrap="wrap"
      >
        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
          Access &amp; Services
        </Typography>
        {card?.version ? (
          <Chip size="small" label={`Card v${card.version}`} variant="outlined" />
        ) : null}
        {mode === "readonly" && incompleteAccess ? (
          <Chip size="small" color="warning" label="Access incomplete" />
        ) : null}
      </Stack>

      {showAcceptGate && mode === "edit" && incompleteAccess ? (
        <Alert severity="error" sx={{ mb: 1.5 }}>
          Confirm Present or Absent for all Access rows before Accept.
        </Alert>
      ) : null}
      {showAcceptGate && mode === "edit" && !incompleteAccess && unknownAddons ? (
        <Alert severity="warning" sx={{ mb: 1.5 }}>
          Some add-ons are still Unknown.
        </Alert>
      ) : null}

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: isMdUp ? "1fr 1fr" : "1fr",
          gap: 2,
        }}
      >
        <Box>
          {accessRows.length > 0
            ? section("Access", accessRows)
            : null}
        </Box>
        <Box>
          {addonRows.length > 0
            ? section(
                "Services & add-ons",
                addonRows,
                mode === "edit" ? (
                  <Button
                    size="small"
                    onClick={handleMarkAddonsAbsent}
                    data-testid="mark-all-addons-absent"
                  >
                    Mark all absent
                  </Button>
                ) : null,
              )
            : null}
        </Box>
      </Box>
    </Box>
  );
};

export default LeadElementChecklist;
